import { useRef, useState } from "react";
import { Link } from "react-router";
import { AxiosError } from "axios";

import { PennButton, PennText } from "@/components/ui";
import {
  useCadGlbUrl,
  useCadStatus,
  useDeleteCadStep,
  useUploadCadStep,
} from "@/hooks/useCad3d";
import { Cad3dStatusChip } from "./Cad3dStatusChip";
import { Cad3dViewer } from "./Cad3dViewer";
import {
  CAD_FILENAME_MAX_CHARS,
  cadFilenameValidationMessage,
} from "@/utils/input-limits";

const ACCEPT = ".step,.stp";

function errorMessage(err: unknown, fallback = "Upload failed."): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as
      | { error?: { message?: string } }
      | undefined;
    return data?.error?.message ?? fallback;
  }
  return fallback;
}

type Cad3dPanelProps = {
  applicationId: string;
  viewerHeight?: number;
  showOpenViewerLink?: boolean;
  showViewer?: boolean;
  /**
   * Read-only mode (issue 3 — task side panel): hides upload + delete + the
   * upload helper text. Keeps the status chip, filename, "Open full viewer →"
   * link and the inline viewer. Mirrors the POC task panel, which previews but
   * never uploads (upload lives in the application editor only).
   */
  readOnly?: boolean;
  /** Label for the viewer entry-point link (default "Open full viewer →"). */
  viewerLinkLabel?: string;
  /** Route the viewer link points at (default "/cad/stepcheck" — single viewer).
   *  Pass "/cad/compare" to open the 2-pane "3D Part Analysis" (similar parts). */
  viewerLinkPath?: string;
  /** Render the viewer link as a solid button (POC "Analyze 3D part" card)
   *  instead of an inline text link. */
  viewerLinkAsButton?: boolean;
  /** Open the viewer link in a new browser tab/window instead of navigating the
   *  current one (used by the RFQ context sidebar so the RFQ stays open). */
  viewerLinkNewWindow?: boolean;
};

export const Cad3dPanel = ({
  applicationId,
  viewerHeight = 320,
  showOpenViewerLink = true,
  showViewer = true,
  readOnly = false,
  viewerLinkLabel = "Open full viewer →",
  viewerLinkPath = "/cad/stepcheck",
  viewerLinkAsButton = false,
  viewerLinkNewWindow = false,
}: Cad3dPanelProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileValidationError, setFileValidationError] = useState<string | null>(
    null,
  );
  const statusQuery = useCadStatus(applicationId);
  const upload = useUploadCadStep();
  const del = useDeleteCadStep();

  const status = statusQuery.data?.status ?? null;
  const hasPreview = statusQuery.data?.hasPreview ?? false;
  const fileName = statusQuery.data?.fileName ?? null;
  const glbQuery = useCadGlbUrl(applicationId, hasPreview);

  const onPick = () => {
    setFileValidationError(null);
    inputRef.current?.click();
  };

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    const filenameError = cadFilenameValidationMessage(file.name);
    if (filenameError) {
      setFileValidationError(filenameError);
      return;
    }
    setFileValidationError(null);
    upload.mutate({ applicationId, file });
  };

  const onDelete = () => {
    if (
      !window.confirm(
        "Delete the uploaded 3D model? This removes the STEP file and its preview.",
      )
    ) {
      return;
    }
    del.mutate({ applicationId });
  };

  const busy = upload.isPending || status === "pending" || status === "running";
  const errorText =
    fileValidationError ||
    (upload.isError && errorMessage(upload.error)) ||
    (del.isError && errorMessage(del.error, "Delete failed.")) ||
    (status === "failed"
      ? (statusQuery.data?.error ?? "The 3D preview could not be generated.")
      : null);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        {!readOnly && (
          <>
            <PennButton
              type="button"
              variant="secondary"
              size="md"
              onClick={onPick}
              disabled={busy}
            >
              {upload.isPending ? "Uploading…" : "Upload STEP file"}
            </PennButton>
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              className="hidden"
              onChange={onFile}
            />
          </>
        )}
        <Cad3dStatusChip status={status} />
        {fileName && (
          <>
            <PennText as="span" size="xs" color="blue_grey">
              {fileName}
            </PennText>
            {!readOnly && (
              <PennButton
                type="button"
                variant="danger"
                size="sm"
                onClick={onDelete}
                // Enabled regardless of analysis status (queued/pending/running) so a
                // stuck or worker-down upload can still be removed; only blocked while
                // a delete or upload is actually in flight.
                disabled={del.isPending || upload.isPending}
              >
                {del.isPending ? "Deleting…" : "Delete"}
              </PennButton>
            )}
          </>
        )}
        {showOpenViewerLink && hasPreview && (
          <Link
            to={`${viewerLinkPath}?applicationId=${encodeURIComponent(applicationId)}`}
            {...(viewerLinkNewWindow
              ? { target: "_blank", rel: "noopener noreferrer" }
              : {})}
            className={
              viewerLinkAsButton
                ? "inline-flex items-center gap-1 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800"
                : "text-xs font-semibold text-penn-700 hover:underline"
            }
          >
            {viewerLinkLabel}
          </Link>
        )}
        {readOnly && !fileName && (
          <PennText as="span" size="xs" color="blue_grey">
            No 3D model uploaded.
          </PennText>
        )}
      </div>

      {!readOnly && (
        <PennText as="p" size="xs" color="blue_grey">
          Accepts STEP files (.step, .stp), up to 50&nbsp;MB, with filenames up
          to {CAD_FILENAME_MAX_CHARS} characters. The 3D preview is generated
          automatically and appears here when ready.
        </PennText>
      )}

      {errorText && (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {errorText}
        </div>
      )}

      {showViewer && hasPreview && glbQuery.data?.url && (
        <Cad3dViewer src={glbQuery.data.url} height={viewerHeight} />
      )}
    </div>
  );
};
